/**
 * The plan's category and its cover, edited from the top bar.
 *
 * ── The cover is DISPLAYED here, not edited ──────────────────────────────────────────────────
 * Covers moved to a server-side pool on 14 August 2026 and this app has no reader for it, so the
 * cover half of this panel is the picture itself beside one line naming what is missing — the CHOICE
 * from that pool, not the cover — see `planMetaView.ts` for the whole reasoning. The sentence says
 * the picture follows the plan's type rather than that it cannot change here, because pressing a
 * chip visibly swaps it: the two questions sit in one panel precisely because the category decides
 * which cover a plan without one falls back to, and a line denying that is contradicted on screen a
 * centimetre away.
 *
 * **The section shows the cover rather than only describing it.** A heading reading "Okładka" over
 * nothing but a sentence asks the trainer to take the app's word for which photograph their plan
 * carries, on the one surface where they cannot go and look.
 *
 * The NAME is edited in place on the `h1` instead. It is the page's own heading, it is the thing the
 * trainer reads first, and the affordance already exists on this screen — a session card's title
 * carries the same dashed underline, and view ADR's note on that affordance applies word for word.
 *
 * ── Every visual byte is ours; Base UI supplies the popup ────────────────────────────────────
 * `Popover` rather than `Dialog`: nothing here is modal, and the trainer can see the plan behind it.
 * The chips are the CREATE modal's own vocabulary rather than a second one — KANON 11/12 draws them
 * there, and this is the same question asked later.
 *
 * **`modal="trap-focus"` and not the default.** The popup is a `dialog`, and with no trap the first
 * Tab out of the category group moved focus to the page behind it and dismissed the panel — a
 * keyboard user could reach the chips and could not leave them without losing the surface. The
 * `'trap-focus'` value is the narrow one: it traps focus and nothing else, so page scroll stays live
 * and a press outside still dismisses, which is what separates it from `modal` and from `Dialog`.
 * `DayEditorDock` uses the same value on the same reasoning. Base UI asks for a `Close` inside the
 * popup whenever focus is trapped, so touch screen readers have an exit — hence the ✕, which is the
 * assign dialog's own control at popover scale rather than a second close vocabulary.
 */

import { Popover } from '@base-ui/react/popover';
import { useId } from 'react';

import { planCategoryColour } from '~/shared/ui/planCategoryColour';

import type { PlanMetaView } from '../model/planMetaView';

import styles from './PlanMetaPopover.module.css';

export interface PlanMetaActions {
  readonly setCategory: (category: PlanMetaView['categories'][number]['key']) => void;
}

interface PlanMetaPopoverProps {
  readonly view: PlanMetaView;
  readonly actions: PlanMetaActions;
  /**
   * Whether the panel is up, and how it is closed.
   *
   * CONTROLLED, and it has no trigger of its own any more. The 07.09 programator mock's top bar
   * draws no cover thumbnail, and nothing the mock does not draw may be visible (owner, 8 September
   * 2026) — so the panel is opened from the plan title's context menu instead, and the title is what
   * it positions against.
   */
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /**
   * The element the popup positions against — the plan's name in the bar.
   *
   * A GETTER rather than a ref, for the reason `PhaseEditorProps.anchor` records: it is resolved at
   * open time, so it cannot be left pointing at an element a re-render replaced.
   */
  readonly anchor: () => HTMLElement | null;
}

export function PlanMetaPopover({ view, actions, open, onOpenChange, anchor }: PlanMetaPopoverProps) {
  const categoryGroup = useId();
  const coverHeadingId = useId();

  return (
    <Popover.Root modal="trap-focus" open={open} onOpenChange={onOpenChange}>
      {/* NO TRIGGER. The cover thumbnail that used to be one is not drawn — the 07.09 programator
        * mock's top bar has no thumbnail — so the panel is opened from the plan title's context menu
        * and positions against the title. See `anchor`. */}
      <Popover.Portal>
        <Popover.Positioner
          className={styles.positioner}
          sideOffset={8}
          align="start"
          anchor={anchor}
        >
          <Popover.Popup className={styles.popup}>
            <div className={styles.head}>
              <Popover.Title className={styles.title}>{view.title}</Popover.Title>
              <Popover.Close className={styles.close} aria-label={view.closeLabel}>
                <span aria-hidden="true">✕</span>
              </Popover.Close>
            </div>

            <fieldset className={styles.field}>
              <legend className={styles.label}>{view.categoryLabel}</legend>
              <div className={styles.chips}>
                {view.categories.map((chip) => (
                  <label
                    key={chip.key}
                    className={`${String(styles.chip)} ${
                      chip.isSelected ? String(styles.chipOn) : ''
                    }`}
                    style={planCategoryColour(chip.key)}
                  >
                    <input
                      className={styles.control}
                      type="radio"
                      name={categoryGroup}
                      value={chip.key}
                      checked={chip.isSelected}
                      onChange={() => {
                        actions.setCategory(chip.key);
                      }}
                    />
                    {/* The dot is the SECOND channel beside the label and the checked state, never
                      * the only one — the six categories are told apart by their words first. */}
                    <span className={styles.chipDot} aria-hidden="true" />
                    {chip.label}
                  </label>
                ))}
              </div>
            </fieldset>

            {/* The cover ITSELF, and a statement rather than a disabled picker. There is no pool
              * this app can read, so a greyed grid over the bundled images would be offering a
              * choice the save cannot express — and a control that refuses every press reads as
              * broken rather than as unavailable. What the section can do is show the picture,
              * which is what its heading promises and what a category press changes.
              *
              * `alt=""` and the whole figure out of the tab order: the image carries no information
              * the sentence beside it does not, and a non-interactive stop in a focus trap is a Tab
              * that appears to do nothing. */}
            <section className={styles.field} aria-labelledby={coverHeadingId}>
              <h3 id={coverHeadingId} className={styles.label}>
                {view.coverLabel}
              </h3>
              <div className={styles.cover}>
                <img className={styles.coverThumb} src={view.coverUrl} alt="" />
                <p className={styles.note}>{view.coverUnavailable}</p>
              </div>
            </section>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
